import React, { lazy, Suspense } from "react";
import { useIntl } from "react-intl";
import { Spin } from "antd";
import PropTypes from "prop-types";

import locales from "../locales";
import ProductOptionsContainer from "./ProductOptionsContainer";

const StatusCard = lazy(() => import("./StatusCard"));
const CancelledSale = lazy(() => import("./CancelledSale"));
const AwaitingManualScreening = lazy(() => import("./AwaitingManualScreening"));
const IdVerificationPending = lazy(() => import("./IdVerificationPending"));

const CartStatus = ({
  cartSummary,
  creditAgreements,
  checkoutRedirect,
  products,
  merchant_name,
  merchantLogoUrl,
  storeDescription,
  storePhone,
  linkExpired,
  awaitingManualScreening,
  idVerificationPending,
  idVerificationUrl,
  status,
  widgetLoaded,
  selectedProductCode,
  isEducationMerchant,
}) => {
  const intl = useIntl();

  const hasPayNow = products?.includes("fp1");

  const Widget = () => {
    return widgetLoaded ? (
      <ProductOptionsContainer
        merchantProducts={products}
        creditAgreements={creditAgreements}
        totalAmount={cartSummary?.totalAmount}
        checkoutRedirect={checkoutRedirect}
        isEducationMerchant={isEducationMerchant}
      />
    ) : (
      <Suspense fallback={<Spin />}>
        <StatusCard
          className="CardWithMarginTop"
          status="error"
          title={intl.formatMessage(locales.widget_error_card_title)}
          description={intl.formatMessage(
            locales.widget_error_card_description
          )}
          button={
            !hasPayNow
              ? {
                  label: intl.formatMessage(locales.widget_error_card_button),
                  link: () => window.location.reload(),
                  style: "button",
                }
              : null
          }
        />
      </Suspense>
    );
  };

  return (
    <Suspense fallback={<Spin />}>
      {(() => {
        if (linkExpired) {
          return (
            <CancelledSale
              className="CardWithMarginTop"
              merchantLogoUrl={merchantLogoUrl}
              merchantName={merchant_name}
              storeDescription={storeDescription}
              storePhone={storePhone}
            />
          );
        }

        if (idVerificationPending && idVerificationUrl) {
          return (
            <IdVerificationPending
              className="CardWithMarginTop"
              idVerificationUrl={idVerificationUrl}
            />
          );
        }

        if (awaitingManualScreening || idVerificationPending) {
          return <AwaitingManualScreening className="CardWithMarginTop" />;
        }

        const isPayNow = selectedProductCode === "fp1";

        switch (status) {
          case "error":
            return (
              <StatusCard
                className="CardWithMarginTop"
                status={status}
                title={intl.formatMessage(
                  locales.shopper_cart_status_error_title
                )}
                description={intl.formatMessage(
                  locales.shopper_cart_status_error_description
                )}
              />
            );
          case "confirmed":
            return (
              <StatusCard
                className="CardWithMarginTop"
                status="confirmed"
                title={intl.formatMessage(
                  locales.shopper_cart_status_confirmed_title_checkout
                )}
                description={`${intl.formatMessage(
                  locales.shopper_cart_status_confirmed_description_amount
                )}${
                  isPayNow
                    ? ` ${intl.formatMessage(
                        locales.shopper_cart_status_confirmed_description_email,
                        {
                          merchant_name,
                        }
                      )}`
                    : ` ${intl.formatMessage(
                        locales.shopper_cart_status_confirmed_description_account
                      )}`
                }`}
                button={
                  !isPayNow
                    ? {
                        label: intl.formatMessage(
                          locales.shopper_cart_status_confirmed_button_personal_account
                        ),
                        link: () =>
                          window.open(
                            intl.formatMessage(locales.link_shopper_app)
                          ),
                      }
                    : null
                }
              />
            );
          case "cancelled":
          // Fall through to reimbursed copy
          // eslint-disable-next-line no-fallthrough
          case "reimbursed":
            return (
              <StatusCard
                className="CardWithMarginTop"
                status={status}
                title={intl.formatMessage(
                  locales.shopper_cart_status_cancelled_title
                )}
                description={intl.formatMessage(
                  locales.shopper_cart_status_cancelled_description
                )}
                button={
                  !isPayNow
                    ? {
                        label: intl.formatMessage(
                          locales.shopper_cart_status_cancelled_button
                        ),
                        link: () =>
                          window.open(intl.formatMessage(locales.link_faqs)),
                      }
                    : null
                }
              />
            );
          case "on_hold":
            return (
              <StatusCard
                className="CardWithMarginTop"
                status="on_hold"
                title={intl.formatMessage(
                  locales.shopper_cart_status_on_hold_title
                )}
                description={intl.formatMessage(
                  locales.shopper_cart_status_on_hold_description
                )}
              />
            );
          default:
            return <Widget />;
        }
      })()}
    </Suspense>
  );
};

CartStatus.propTypes = {
  cartSummary: PropTypes.object.isRequired,
  creditAgreements: PropTypes.object.isRequired,
  checkoutRedirect: PropTypes.func.isRequired,
  products: PropTypes.array.isRequired,
  merchant_name: PropTypes.string,
  merchantLogoUrl: PropTypes.string,
  storeDescription: PropTypes.string,
  storePhone: PropTypes.string,
  linkExpired: PropTypes.bool,
  awaitingManualScreening: PropTypes.bool,
  idVerificationPending: PropTypes.bool,
  idVerificationUrl: PropTypes.string,
  status: PropTypes.string,
  widgetLoaded: PropTypes.bool,
  selectedProductCode: PropTypes.string,
  isEducationMerchant: PropTypes.bool,
};

CartStatus.defaultProps = {
  merchant_name: "",
  merchantLogoUrl: "",
  storeDescription: "",
  storePhone: "",
  linkExpired: false,
  awaitingManualScreening: false,
  idVerificationPending: false,
  idVerificationUrl: "",
  status: null,
  widgetLoaded: null,
  selectedProductCode: "",
  isEducationMerchant: false,
};

export default CartStatus;
